import React, { useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { IoRocketOutline, IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { FiGitBranch, FiLayers, FiList, FiPackage, FiSettings } from 'react-icons/fi';
import { BsCheckCircle } from 'react-icons/bs';
import { useWorkflowRunTracking } from '../hooks/useWorkflowRunTracking';
import WorkflowRunProgressPanel from '../components/WorkflowRunProgressPanel';

const STEPS = [
  { id: 1, label: 'Release', sub: 'Version & notes' },
  { id: 2, label: 'Channel', sub: 'How it ships' },
  { id: 3, label: 'Build', sub: 'Branch & config' },
  { id: 4, label: 'Run', sub: 'Confirm' },
];

const RELEASE_OPTIONS = [
  {
    value: 'production',
    title: 'Production',
    hint: 'Updates patches/latest.json (customer-facing).',
    accent: 'from-emerald-500/15 to-teal-600/10 border-emerald-200',
    ring: 'ring-emerald-500/30',
  },
  {
    value: 'dev',
    title: 'Development',
    hint: 'Uses patches/latest-dev.json only.',
    accent: 'from-amber-500/15 to-orange-600/10 border-amber-200',
    ring: 'ring-amber-500/30',
  },
];

const PATCH_TYPE_OPTIONS = [
  {
    value: 'api-and-sql',
    title: 'API + SQL',
    hint: 'packageMode: api-sql — build API and reserve scripts folder.',
    pkg: 'api-sql',
  },
  {
    value: 'api-only',
    title: 'API only',
    hint: 'packageMode: api — no SQL scripts in patch.',
    pkg: 'api',
  },
  {
    value: 'sql-only',
    title: 'SQL only',
    hint: 'packageMode: sql — scripts folder for patch.sql.',
    pkg: 'sql',
  },
];

const APPSETTINGS_OPTIONS = [
  { value: 'appsettings.json', title: 'appsettings.json', hint: 'Production-style config from repo.' },
  {
    value: 'appsettings.example.json',
    title: 'appsettings.example.json',
    hint: 'Safer default for patches.',
  },
];

function workflowFileNameFromPath(pathStr) {
  if (!pathStr || typeof pathStr !== 'string') return '';
  const parts = pathStr.split('/');
  return parts[parts.length - 1] || '';
}

function isOnPremPatchWorkflow(w) {
  return /^create-onprem-patch\.yml$/i.test(workflowFileNameFromPath(w?.path));
}

/** Desk dispatch body.workflowId: prefer YAML file name; fall back to numeric id from the list. */
function deskWorkflowIdForDispatch(w) {
  if (!w) return null;
  const file = w.fileName || workflowFileNameFromPath(w.path);
  if (file) return file;
  if (w.id != null) return w.id;
  return null;
}

/**
 * Local API proxy (server/index.js). Use an absolute URL in production/Electron builds.
 */
function dispatchBaseUrl() {
  const explicit = process.env.REACT_APP_PATCH_DISPATCH_URL;
  if (explicit) return String(explicit).replace(/\/$/, '');
  const port = process.env.REACT_APP_PATCH_SERVER_PORT || '8787';
  return `http://127.0.0.1:${port}`;
}

/** Default branch/ref sent with workflow_dispatch (must exist on the remote for that workflow). */
const DEFAULT_WORKFLOW_REF = process.env.REACT_APP_DEFAULT_WORKFLOW_REF || 'main';

function toastWorkflowRunOutcome(run) {
  if (!run) return;
  const label = run.display_title || run.name || 'Workflow run';
  if (run.status === 'completed' && run.conclusion === 'success') {
    toast.success(
      <div className="flex flex-col gap-0.5 pr-1 text-left">
        <span className="font-semibold text-emerald-950">Run completed successfully</span>
        <span className="text-sm font-normal text-emerald-900/90">{label}</span>
      </div>,
      {
        autoClose: 4500,
        transition: Slide,
        className:
          '!rounded-xl !bg-gradient-to-r !from-emerald-50 !to-teal-50 !border !border-emerald-200/90 !shadow-lg',
        bodyClassName: '!p-3',
      },
    );
    return;
  }
  toast.error(
    <div className="flex flex-col gap-1 pr-1 text-left">
      <span className="font-semibold text-red-950">Workflow did not succeed</span>
      <span className="text-sm text-red-900/95">
        Status: <strong>{run.status ?? '—'}</strong>
        {run.conclusion != null && run.conclusion !== '' ? (
          <>
            {' '}
            · result: <strong>{run.conclusion}</strong>
          </>
        ) : null}
      </span>
      <span className="text-xs text-red-800/85">{label}</span>
    </div>,
    {
      autoClose: 7000,
      transition: Slide,
      className:
        '!rounded-xl !bg-gradient-to-r !from-red-50 !to-rose-50 !border !border-red-200/90 !shadow-lg',
      bodyClassName: '!p-3',
    },
  );
}

function toastRunFailureMessage(msg) {
  toast.error(
    <div className="pr-1 text-left">
      <span className="font-semibold text-red-950">Run tracking failed</span>
      <p className="mt-1 text-sm text-red-900/95">{msg}</p>
    </div>,
    {
      autoClose: 6000,
      transition: Slide,
      className:
        '!rounded-xl !bg-gradient-to-r !from-red-50 !to-rose-50 !border !border-red-200/90 !shadow-lg',
      bodyClassName: '!p-3',
    },
  );
}

export default function CreateOnPremPatch() {
  const [phase, setPhase] = useState('select');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [deskWorkflows, setDeskWorkflows] = useState([]);
  const [deskLoading, setDeskLoading] = useState(false);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [apiReady, setApiReady] = useState(null);
  const [repoCtx, setRepoCtx] = useState({ owner: null, repo: null });

  const proxyBase = useMemo(() => dispatchBaseUrl(), []);
  const deskRepo = useMemo(
    () => ({ owner: repoCtx.owner, repo: repoCtx.repo }),
    [repoCtx.owner, repoCtx.repo],
  );
  const progress = useWorkflowRunTracking(proxyBase, deskRepo);

  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [releaseMode, setReleaseMode] = useState('production');
  const [patchType, setPatchType] = useState('api-and-sql');
  const [branch, setBranch] = useState(DEFAULT_WORKFLOW_REF);
  const [appsettingsSource, setAppsettingsSource] = useState('appsettings.example.json');

  const needsApi = patchType === 'api-and-sql' || patchType === 'api-only';

  const packageMode = useMemo(() => {
    const m = {
      'api-only': 'api',
      'api-and-sql': 'api-sql',
      'sql-only': 'sql',
    };
    return m[patchType] || '';
  }, [patchType]);

  const latestPointer = releaseMode === 'dev' ? 'patches/latest-dev.json' : 'patches/latest.json';

  const validateStep = (s) => {
    if (s === 1) {
      if (!version.trim()) {
        toast.error('Enter a patch version (e.g. 1.2.0)');
        return false;
      }
      if (!/^\d+\.\d+\.\d+/.test(version.trim())) {
        toast.warn('Version should look like semver (e.g. 1.2.0)');
      }
      if (!description.trim()) {
        toast.error('Add release notes for this patch');
        return false;
      }
    }
    if (s === 3) {
      if (!branch.trim()) {
        toast.error('Branch is required');
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((x) => Math.min(4, x + 1));
  };

  const back = () => {
    if (phase === 'configure' && selectedWorkflow && isOnPremPatchWorkflow(selectedWorkflow)) {
      if (step > 1) {
        setStep((x) => x - 1);
        return;
      }
    }
    progress.reset();
    setPhase('select');
    setSelectedWorkflow(null);
  };

  const loadDeskWorkflows = useCallback(async () => {
    setDeskLoading(true);
    try {
      const base = dispatchBaseUrl();
      const { data: health } = await axios.get(`${base}/api/health`, { timeout: 5000 });
      setApiReady(Boolean(health?.deskReady));
      const owner = health?.owner;
      const repo = health?.repo;
      setRepoCtx({ owner: owner || null, repo: repo || null });
      if (!owner || !repo) {
        toast.error('Set DESK_REPO_OWNER and DESK_REPO_NAME on the patch server.');
        setDeskWorkflows([]);
        return;
      }
      const res = await axios.get(`${base}/api/desk/workflows`, {
        params: { owner, repo },
        timeout: 20000,
        validateStatus: () => true,
      });
      if (res.status === 501) {
        toast.error(
          res.data?.error || 'Desk API not configured. Set DESK_API_KEY in server .env',
        );
        setDeskWorkflows([]);
        return;
      }
      if (res.status !== 200) {
        toast.error(res.data?.error || `Could not list Desk workflows (HTTP ${res.status})`);
        setDeskWorkflows([]);
        return;
      }
      setDeskWorkflows(res.data.workflows || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Failed to load workflows from Desk');
      setDeskWorkflows([]);
    } finally {
      setDeskLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeskWorkflows();
  }, [loadDeskWorkflows]);

  const checkApi = async () => {
    try {
      const { data } = await axios.get(`${dispatchBaseUrl()}/api/health`, { timeout: 5000 });
      setApiReady(Boolean(data?.deskReady));
      setRepoCtx({ owner: data?.owner || null, repo: data?.repo || null });
      if (!data?.deskApiKeyConfigured) {
        toast.warning(
          'DESK_API_KEY is missing on the server — add it to server/.env or root .env.',
        );
      } else if (!data?.repoConfigured) {
        toast.warning('Set DESK_REPO_OWNER and DESK_REPO_NAME on the patch server.');
      }
    } catch {
      setApiReady(false);
      toast.error(
        'Cannot reach API proxy. Run npm run server (or npm run dev) and check port 8787.',
      );
    }
  };

  const submitGeneric = async () => {
    if (!selectedWorkflow) {
      toast.error('Choose a workflow first');
      return;
    }
    const workflowId = deskWorkflowIdForDispatch(selectedWorkflow);
    if (workflowId == null || (typeof workflowId === 'string' && !workflowId.trim())) {
      toast.error('Could not resolve workflow id — refresh the list from Desk');
      return;
    }
    if (!branch.trim()) {
      toast.error('Branch (ref) is required');
      return;
    }
    setSubmitting(true);
    try {
      const result = await progress.dispatchAndTrack({
        workflowId,
        ref: branch.trim(),
        inputs: {},
      });
      if (result?.run) toastWorkflowRunOutcome(result.run);
    } catch (e) {
      toastRunFailureMessage(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!selectedWorkflow) {
      toast.error('Choose a workflow first');
      return;
    }
    const workflowId = deskWorkflowIdForDispatch(selectedWorkflow);
    if (workflowId == null || (typeof workflowId === 'string' && !workflowId.trim())) {
      toast.error('Could not resolve workflow id — refresh the list from Desk');
      return;
    }
    if (!validateStep(1) || !validateStep(3)) return;
    setSubmitting(true);
    try {
      const result = await progress.dispatchAndTrack({
        workflowId,
        ref: branch.trim(),
        inputs: {
          version: version.trim(),
          description: description.trim(),
          release_mode: releaseMode,
          patch_type: patchType,
          branch: branch.trim(),
          appsettings_source: appsettingsSource,
        },
      });
      if (result?.run) toastWorkflowRunOutcome(result.run);
    } catch (e) {
      toastRunFailureMessage(e instanceof Error ? e.message : 'Run failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="box-border w-full min-w-0 max-w-full p-4 md:p-6">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        closeOnClick
        pauseOnHover
        theme="light"
      />
      <div className="w-full min-w-0 space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-bold text-gray-800 flex flex-wrap items-center gap-3">
              <FiPackage size={32} className="shrink-0 text-blue-600" />
              On-prem patch
            </h1>
            <p className="text-gray-500 mt-2 max-w-4xl text-sm leading-relaxed">
              Pick a workflow from Fluxion Desk, configure the run, and dispatch through the local API proxy. Secrets stay
              in <span className="font-mono text-xs text-gray-700">server/.env</span> only.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={checkApi}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
            >
              <FiSettings className="h-4 w-4 shrink-0 text-blue-600" />
              Check API
              {apiReady === true && (
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                  OK
                </span>
              )}
              {apiReady === false && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                  Down
                </span>
              )}
            </button>
          </div>
        </header>

        {phase === 'select' && (
          <section
            className="w-full min-w-0 rounded-xl border border-gray-100 bg-white p-4 shadow-sm md:p-6"
            aria-label="Choose workflow"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiList className="text-blue-600 shrink-0" size={24} />
                  Choose a workflow
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Loaded from Fluxion Desk for the repo in <span className="font-mono text-xs">server/.env</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={loadDeskWorkflows}
                disabled={deskLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-60 shrink-0"
              >
                {deskLoading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                ) : (
                  <FiList className="h-4 w-4 text-blue-600" />
                )}
                Refresh list
              </button>
            </div>
            {deskLoading && deskWorkflows.length === 0 ? (
              <p className="text-sm text-gray-600 py-4">Loading workflows…</p>
            ) : deskWorkflows.length === 0 ? (
              <p className="text-sm text-gray-600 py-2">
                No workflows returned. Set <span className="font-mono">DESK_API_KEY</span>,{' '}
                <span className="font-mono">DESK_REPO_OWNER</span>, and{' '}
                <span className="font-mono">DESK_REPO_NAME</span> on the server, then use{' '}
                <span className="font-mono">Check API</span> or <span className="font-mono">Refresh list</span>.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 w-full">
                {deskWorkflows.map((w) => (
                  <li key={w.id} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => {
                        progress.reset();
                        setSelectedWorkflow(w);
                        setPhase('configure');
                        setStep(1);
                        setBranch(DEFAULT_WORKFLOW_REF);
                      }}
                      className="flex h-full w-full min-w-0 flex-col rounded-xl border border-gray-200 bg-gray-50/80 p-4 text-left transition hover:border-blue-300 hover:bg-white hover:shadow-sm"
                    >
                      <span className="line-clamp-2 text-sm font-semibold text-gray-900">{w.name}</span>
                      <span className="mt-1 break-all font-mono text-xs text-gray-500">{w.path}</span>
                      {w.state ? (
                        <span className="mt-3 inline-flex w-fit rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                          {w.state}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {phase === 'configure' && selectedWorkflow && !isOnPremPatchWorkflow(selectedWorkflow) && (
          <div className="min-w-0 w-full rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 bg-gray-50 px-4 py-4 md:px-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <IoRocketOutline className="shrink-0 text-2xl text-blue-600" />
                  <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-800 leading-tight">Configure run</h2>
                    <p className="truncate text-sm text-gray-500 mt-0.5">{selectedWorkflow.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    progress.reset();
                    setPhase('select');
                    setSelectedWorkflow(null);
                  }}
                  className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Change workflow
                </button>
              </div>
            </div>
            <div className="space-y-3 p-4 md:p-6">
              <p className="font-mono text-xs text-gray-600 break-all">{selectedWorkflow.path}</p>
              <label className="block">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                  <FiGitBranch className="h-4 w-4 shrink-0 text-blue-600" />
                  Branch / ref
                </span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  placeholder={DEFAULT_WORKFLOW_REF}
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                />
              </label>
              <p className="text-xs leading-relaxed text-gray-500">
                This dispatch sends an empty <span className="font-mono">inputs</span> object. If Desk rejects the request, the
                workflow may expect different input names—match them to your Action&apos;s{' '}
                <span className="font-mono">workflow_dispatch</span> inputs.
              </p>
            </div>
            <div className="border-t border-gray-100 px-4 md:px-6">
              <WorkflowRunProgressPanel
                state={progress.state}
                error={progress.error}
                title="Run progress"
                onReset={() => progress.reset()}
              />
            </div>
            <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 md:px-6 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={submitting}
                onClick={submitGeneric}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Running…
                  </>
                ) : (
                  <>
                    <IoRocketOutline className="text-lg" />
                    Run workflow
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {phase === 'configure' && selectedWorkflow && isOnPremPatchWorkflow(selectedWorkflow) && (
          <div className="min-w-0 space-y-4">
            <nav aria-label="Steps">
              <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STEPS.map((s) => {
                  const active = step === s.id;
                  const done = step > s.id;
                  return (
                    <li key={s.id} className="min-w-0">
                      <div
                        className={`flex min-w-0 items-center gap-2 rounded-xl border px-2.5 py-2 transition ${
                          active
                            ? 'border-blue-300 bg-white shadow-sm ring-2 ring-blue-500/20'
                            : done
                              ? 'border-green-200 bg-white'
                              : 'border-gray-200 bg-gray-50/80'
                        }`}
                      >
                        <div
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            active
                              ? 'bg-blue-600 text-white'
                              : done
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {done ? <BsCheckCircle className="text-base" /> : s.id}
                        </div>
                        <div className="min-w-0 py-0.5">
                          <p className="truncate text-xs font-semibold text-gray-900 sm:text-sm">{s.label}</p>
                          <p className="truncate text-[10px] text-gray-500 sm:text-xs">{s.sub}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </nav>

            <div className="min-w-0 w-full rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 bg-gray-50 px-4 py-4 md:px-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <IoRocketOutline className="shrink-0 text-2xl text-blue-600" />
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-gray-800 leading-tight">Create patch run</h2>
                      <p className="truncate text-sm text-gray-500 mt-0.5" title={selectedWorkflow?.path}>
                        {selectedWorkflow?.name}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Step {step} of {STEPS.length}
                        {step === 4 ? ' · Confirm' : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      progress.reset();
                      setPhase('select');
                      setSelectedWorkflow(null);
                    }}
                    className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Change workflow
                  </button>
                </div>
              </div>

              <div className="p-4 md:p-6">
            {step === 1 && (
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Patch version</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="e.g. 1.2.0"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Release notes</span>
                  <textarea
                    className="mt-1 min-h-[88px] w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder="What changed, risk notes, rollout hints…"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </label>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                    <FiLayers className="h-4 w-4 shrink-0 text-blue-600" />
                    Release channel
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">Which &quot;latest&quot; pointer is updated.</p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {RELEASE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setReleaseMode(opt.value)}
                        className={`rounded-xl border-2 p-3 text-left text-sm transition ${
                          releaseMode === opt.value
                            ? `bg-gradient-to-br ${opt.accent} shadow-md ${opt.ring} ring-2`
                            : 'border-gray-200 bg-gray-50/80 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-900">{opt.title}</p>
                        <p className="mt-1 text-xs leading-relaxed text-gray-600">{opt.hint}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Patch contents</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Maps to workflow <span className="font-mono">packageMode</span>.
                  </p>
                  <div className="mt-2 grid gap-2 md:grid-cols-3">
                    {PATCH_TYPE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setPatchType(opt.value)}
                        className={`rounded-xl border-2 p-3 text-left text-sm transition ${
                          patchType === opt.value
                            ? 'border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-500/25'
                            : 'border-gray-200 bg-gray-50/80 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-900">{opt.title}</p>
                        <p className="mt-1 text-xs leading-snug text-gray-600">{opt.hint}</p>
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-blue-700">
                          {opt.pkg}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <label className="block">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                    <FiGitBranch className="h-4 w-4 shrink-0 text-blue-600" />
                    Branch to build from
                  </span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    placeholder={DEFAULT_WORKFLOW_REF}
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Sent as dispatch <span className="font-mono">ref</span> (use a branch/tag that exists for this workflow—often{' '}
                    <span className="font-mono">dev</span>) and as <span className="font-mono">inputs.branch</span> where the
                    workflow expects it.
                  </p>
                </label>

                {needsApi ? (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Appsettings in API patch</p>
                    <p className="mt-0.5 text-xs text-gray-500">When the build includes the API.</p>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      {APPSETTINGS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setAppsettingsSource(opt.value)}
                          className={`rounded-xl border-2 p-3 text-left transition ${
                            appsettingsSource === opt.value
                              ? 'border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-500/20'
                              : 'border-gray-200 bg-gray-50/80 hover:border-gray-300'
                          }`}
                        >
                          <p className="font-mono text-[11px] font-semibold text-gray-900">{opt.title}</p>
                          <p className="mt-1 text-xs text-gray-600">{opt.hint}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
                    SQL-only: appsettings is ignored; workflow still gets its default.
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 md:p-4">
                  <dl className="grid gap-2.5 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Version</dt>
                      <dd className="mt-1 font-semibold text-gray-900">{version}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Branch</dt>
                      <dd className="mt-1 font-mono text-gray-900">{branch}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Channel</dt>
                      <dd className="mt-1 text-gray-900">{releaseMode}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Patch type</dt>
                      <dd className="mt-1 text-gray-900">{patchType}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">packageMode</dt>
                      <dd className="mt-1 font-mono text-blue-700">{packageMode}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Latest pointer</dt>
                      <dd className="mt-1 font-mono text-xs text-gray-800">{latestPointer}</dd>
                    </div>
                    {needsApi && (
                      <div className="sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Appsettings</dt>
                        <dd className="mt-1 font-mono text-sm text-gray-900">{appsettingsSource}</dd>
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Notes</dt>
                      <dd className="mt-1 whitespace-pre-wrap text-gray-800">{description}</dd>
                    </div>
                  </dl>
                </div>
                <p className="text-xs leading-relaxed text-gray-500">
                  Runs may take a moment to appear. Workflow file:{' '}
                  <span className="font-mono">{workflowFileNameFromPath(selectedWorkflow?.path)}</span>
                  . Repo from <span className="font-mono">server/.env</span>.
                </p>
              </div>
            )}
              </div>

              <div className="border-t border-gray-100 px-4 md:px-6">
                <WorkflowRunProgressPanel
                  state={progress.state}
                  error={progress.error}
                  title="Run progress"
                  onReset={() => progress.reset()}
                />
              </div>

              <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-4 py-3 md:px-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={back}
                    className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                  >
                    <IoChevronBack />
                    {step > 1 ? 'Back' : 'Workflows'}
                  </button>
                </div>
                <div className="flex gap-2 sm:ml-auto">
                  {step < 4 ? (
                    <button
                      type="button"
                      onClick={next}
                      className="inline-flex items-center gap-0.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                    >
                      Continue
                      <IoChevronForward />
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={submit}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-green-700 disabled:opacity-60"
                    >
                      {submitting ? (
                        <>
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Running…
                        </>
                      ) : (
                        <>
                          <IoRocketOutline className="text-lg" />
                          Run workflow
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
